/**
 * Popup around FieldEvidenceView showing a field's evidence. "Edit" opens an inline
 * editor panel (reusing FieldDropdown) that saves via updateSessionFields — a plain
 * human override, without the validation validation flow.
 */

import React, { useMemo, useState } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { useSessionWriteAccess } from '../../../hooks/useSessionWriteAccess';
import { updateSessionFields } from '../../../store/sessionSlice';
import { showError, showSuccess } from '../../../store/toastsSlice';
import {
  FieldValue,
  applyGroupedChildInit,
  fieldValuesEqual,
  getFieldGroup,
  getFieldLabel,
  parentValueOf,
  mergeWithHumanOverrides,
} from '../../../utils/fieldUtils';
import { FONT_SIZE_BODY, TEXT_COLOR_PRIMARY } from '../cssConstants';
import FieldEvidenceView from './FieldEvidenceView';
import FieldDropdown from './FieldDropdown';

interface EvidenceModalProps {
  open: boolean;
  onClose: () => void;
  fieldName: string;
}

const EvidenceModal: React.FC<EvidenceModalProps> = ({ open, onClose, fieldName }) => {
  const dispatch = useAppDispatch();
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const currentSession = useAppSelector((state) => state.session.currentSession);
  // A read-only auditor gets the evidence view without the inline override editor.
  const { canEdit } = useSessionWriteAccess(currentSession);

  const [editing, setEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<Partial<Record<string, FieldValue>>>({});
  const [saving, setSaving] = useState(false);

  // Show a clicked field together with its dependency group (e.g. principal + detailed body part).
  const fieldNames = getFieldGroup(fieldsConfig, fieldName);

  // Current values the reviewer edits (submitted > overrides > extracted).
  const merged = useMemo(
    () =>
      mergeWithHumanOverrides(
        currentSession?.extracted_fields || {},
        currentSession?.human_overrides_fields,
        currentSession?.submitted_fields,
      ) as Record<string, FieldValue>,
    [currentSession],
  );

  const startEditing = () => {
    const seed: Partial<Record<string, FieldValue>> = {};
    for (const f of fieldNames) if (merged[f] !== undefined) seed[f] = merged[f];
    setEditedValues(seed);
    setEditing(true);
  };

  const handleChange = (name: string, value: FieldValue) => {
    setEditedValues((prev) => applyGroupedChildInit(fieldsConfig, name, value, prev));
  };

  const handleSave = async () => {
    if (!currentSession) return;
    // Only send fields the reviewer actually changed.
    const fields: Record<string, FieldValue> = {};
    for (const [key, value] of Object.entries(editedValues)) {
      if (value === undefined) continue;
      if (fieldValuesEqual(value, merged[key])) continue;
      fields[key] = value;
    }
    if (Object.keys(fields).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await dispatch(updateSessionFields({ sessionId: currentSession.session_id, fields })).unwrap();
      dispatch(showSuccess('Fields updated successfully'));
      setEditing(false);
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to update fields'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  const editorPanel = (
    <div style={{ height: '100%', overflow: 'auto', paddingLeft: '16px', paddingRight: '4px' }}>
      <div style={{ fontSize: FONT_SIZE_BODY, fontWeight: 600, color: TEXT_COLOR_PRIMARY, marginBottom: '16px' }}>
        Edit
      </div>
      {fieldNames.map((f) => {
        const parentValue = parentValueOf(fieldsConfig, f, editedValues);
        return (
          <FieldDropdown
            key={f}
            fieldName={f}
            value={editedValues[f]}
            parentValue={parentValue}
            label={getFieldLabel(fieldsConfig, f)}
            onChange={handleChange}
          />
        );
      })}
    </div>
  );

  return (
    <Modal open={open} onClose={handleClose} width={editing ? '92vw' : undefined} scrollContainer="modal" closeLabel="Close">
      {editing ? (
        <div style={{ width: '100%', height: '68vh', display: 'flex', gap: '16px', overflow: 'hidden' }}>
          <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '68%', minWidth: 0, overflow: 'hidden' }}>
            <FieldEvidenceView fieldNames={fieldNames} />
          </div>
          <div style={{ flexGrow: 0, flexShrink: 1, flexBasis: '30%', minWidth: 0, borderLeft: '1px solid #e0e0e0' }}>
            {editorPanel}
          </div>
        </div>
      ) : (
        <div style={{ minWidth: '70vw', maxWidth: '85vw', minHeight: '60vh', maxHeight: '80vh' }}>
          <FieldEvidenceView fieldNames={fieldNames} onEdit={startEditing} editDisabled={!canEdit} />
        </div>
      )}
      <ModalFooter>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {editing ? (
            <>
              <Button type="secondary" size="small" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="primary" size="small" onClick={handleSave} disabled={saving}>
                Save
              </Button>
            </>
          ) : (
            <Button type="secondary" onClick={handleClose}>
              Close
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default EvidenceModal;
